<template>
  <div v-bind="$attrs">
    <SloLoader
      :loading="loading"
      :class="{
       'flex-col w-100': true,
       'gap-md sm:flex-row sm:items-center md:gap-lg': !stack
      }"
    >
      <SloProductImage
        v-if="!hideImage" :source="item.variation_image || item.product_image"
        class="product-variation-thumbnail"
      />
      <div
        v-if="productContext.productType !== 'part'"
        :class="{
          'flex-column flex-1': true,
          'mt-md': !hideImage,
          'sm:mt-0': !hideImage && !stack,
        }"
      >
        <div class="flex flex-wrap items-baseline -m-xs">
          <h4 class="text-lg md:text-xl fw-strong border-xs">
            {{ item.variation_title }}
          </h4>
          <p
            class="text-base md:text-lg c-gray-700 border-xs"
            v-if="shouldDisplaySku"
            v-html="'Code ' + item.variation_sku"
          />
          <span
            class="label label-danger discontinued self-center p-sm ml-sm"
            v-if="item.variation_discontinued">
            {{ 'Discontinued' | t }}
          </span>
        </div>
        <p
          class="text-base md:text-sm c-gray-900 mt-sm text-right discontinued-replacements"
          v-if="item.variation_discontinued && item.variation_discontinued_replacements"
        >
          {{ 'Replaced by' | t }}
          <span v-html="getDiscontinuedReplacementsHtml(item)"></span>
        </p>
        <p
          class="mt-sm text-sm sm:text-base md:text-lg"
          v-html="item.variation_description"
        />
        <div class="flex flex-col items-start justify-start mt-md sm:flex-row sm:items-center">
          <ExpandButton
            v-if="
              item.variation_compliances_certifications &&
              item.variation_compliances_certifications.length > 0
            "
            @click="compliancesAndCertificationsExpanded = !compliancesAndCertificationsExpanded"
            :expanded="compliancesAndCertificationsExpanded"
          >
            {{ 'Compliances & Certifications' | t }}
          </ExpandButton>
          <AddToProjectButton
            v-if="productContext.productType !== 'part'"
            :disabled="item.variation_discontinued"
            class="hide mt-md sm:mt-0 sm:ml-md sm:inline-flex"
            type="variation"
            :item-id="item.variation_id"
            :item-thumbnail="getItemThumbnail()"
          />
        </div>
        <ul class="reset flex flex-wrap mt-sm -mx-xs" v-if="compliancesAndCertificationsExpanded">
          <li
            class="border-xs"
            v-for="compliance of normalizeCompliances(item)"
            :key="compliance"
          >
            <span class="py-xs px-sm bg-gray-100 rounded-full" v-html="compliance" />
          </li>
        </ul>
        <div class="mt-md sm:mt-0 sm:ml-md sm:hide">
          <AddToProjectButton
            v-if="productContext.productType !== 'part'"
            :disabled="item.variation_discontinued"
            type="variation"
            :item-id="item.variation_id"
            :item-thumbnail="getItemThumbnail()"
          />
        </div>
        <SloProductMeta
          v-if="product && showEstimatedShipDate"
          :product="{
            estimatedShipDate: product?.estimatedShipDate,
            restrictedMessage: product?.restrictedMessage,
            price: null
          }"
        />
      </div>
      <div v-if="isOrderable" class="w-100 sm:w-auto">
        <div class="product-actions flex flex-col gap-md">
          <SubscriptionPDPAddToCart
            v-if="shouldDisplaySubscriptionUI"
            :product="product"
            :discontinued="item.variation_discontinued"
          />
          <div v-else>
            <SloProductMeta
              :product="{
                estimatedShipDate: showPriceInfo ? product?.estimatedShipDate : null,
                restrictedMessage: product?.restrictedMessage,
                price: {formattedValue: product?.price?.formattedValue}
              }"
            >
              <span class="unit-of-measure">{{ product?.uom }}</span>
            </SloProductMeta>
            <SloAddToCart
              :sku="item.variation_sku"
              :show-number-input="showNumberInput"
              :subscription-frequencies="product?.subscriptionFrequencies"
              list-name="pdp_page"
            />
          </div>
          <p v-if="showPriceInfo" class="text-sm mt-sm">
            {{ 'Purchased from Sloan Marketplace LLC' | t }}
          </p>
          <p
            v-if="!product?.isReturnable || !product?.isCancellable"
            class="text-sm mt-sm"
          >
            This item is
            <a v-if="!product?.isReturnable" href="https://legal.sloan.com/sloan-marketplace.html#return-policy" target="_blank">
              not returnable
            </a>
            <span v-if="!product?.isCancellable">and</span>
            <a v-if="!product?.isCancellable" href="https://legal.sloan.com/sloan-marketplace.html#sink-cancellation-and-return-policy" target="_blank">
              not cancellable
            </a>
          </p>
          <SloButton
            v-if="!showPriceInfo && showDownloadModelSpec"
            fill="outline"
            target="_blank"
            @click="trackClick()"
            :href="getSpecSheetLink(item.variation_sku)"
          >
            <span class="no-wrap">{{ 'Download Model Spec' | t }}</span>
          </SloButton>
        </div>
        <SloProp65Warning v-if="showProp65Warning" />
      </div>
      <VariationActions
        v-else
        :sku="item.variation_sku"
        :product-type="productContext.productType"
        :product-title="item.variation_title ?? item.product_title"
        :product-description="item.variation_description ?? item.product_description"
        :product-context="productContext"
        :channel-advisor-enabled="item.variation_channel_advisor_enabled"
        :show-download-model-spec="showDownloadModelSpec"
        :show-where-to-buy="showWhereToBuy"
        :show-channel-advisor="showChannelAdvisor"
        :stack="stack"
      />
    </SloLoader>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {getLangcode, getSpecSheetPath, getImagesArray, isProductOrderable} from '../../main/utils';
import {marketplaceApi} from '../../marketplace/apis/marketplace';
import ExpandButton from './ExpandButton.vue';
import AddToProjectButton from './AddToProjectButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloProductGate from '../../../lib/components/SloProductGate.vue';
import VariationActions from './VariationActions.vue';
import SloProp65Warning from '../../../lib/components/SloProp65Warning.vue';
import SubscriptionPDPAddToCart from '../../marketplace/components/SubscriptionPDPAddToCart.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';

export default {
  inject: ['productContext'],
  components: {
    SloLabel,
    SloField,
    SloInput,
    SubscriptionPDPAddToCart,
    ExpandButton,
    AddToProjectButton,
    SloLoader,
    SloAddToCart,
    SloNumberInput,
    SloButton,
    SloProductImage,
    SloProductMeta,
    SloProductGate,
    SloProp65Warning,
    VariationActions
  },
  filters: {
    t
  },
  data() {
    return {
      loading: true,
      product: null,
      isOrderable: false,
      compliancesAndCertificationsExpanded: false
    };
  },
  props: {
    stack: {type: Boolean, default: false},
    hideImage: Boolean,
    item: {type: Object},
    trackView: {type: Boolean, default: false},
    showNumberInput: {type: Boolean, default: false},
    showDownloadModelSpec: {type: Boolean, default: true},
    showWhereToBuy: {type: Boolean, default: true},
    showChannelAdvisor: {type: Boolean, default: true},
    showPriceInfo: {type: Boolean, default: false},
    showEstimatedShipDate: {type: Boolean, default: false},
    showProp65Warning: {type: Boolean, default: false},
    shouldDisplaySubscriptionInput: {type: Boolean, default: false}
  },
  computed: {
    langcode() {
      return getLangcode(this.$route.params.langcode);
    },
    shouldDisplaySku() {
      return this.productContext.productType !== 'part' &&
        (this.productContext.productType !== 'sink'
        || this.item.product_product_line === 'Mobile Handwashing Station');
    },
    shouldDisplaySubscriptionUI() {
      return this.product?.isSubscribable && this.shouldDisplaySubscriptionInput;
    }
  },
  async created() {
    const results = await marketplaceApi.productSearch(this.item.variation_sku);
    if (results?.data?.products.length) {
      this.product = results.data.products[0];
      this.isOrderable = isProductOrderable(this.product);
    }
    this.loading = false;
  },
  methods: {
    getItemThumbnail() {
      const images = getImagesArray(this.item.variation_image || this.item.product_image);
      return images[0]?.src;
    },
    getSpecSheetLink(sku) {
      return getSpecSheetPath(sku, this.langcode);
    },
    normalizeCompliances(item) {
      return Array.isArray(item.variation_compliances_certifications)
        ? item.variation_compliances_certifications
        : item.variation_compliances_certifications.split(', ').map(v => v.trim());
    },
    trackClick() {
      const {analytics} = this.productContext;
      window.ga('send', 'event', {
        eventCategory: analytics.eventCategory,
        eventAction: 'download',
        eventLabel: analytics.eventLabel
      });
    },
    getDiscontinuedReplacementsHtml(item) {
      if (!Object.prototype.hasOwnProperty.call(item, 'variation_discontinued_replacements'))
        return;

      return JSON.parse(item.variation_discontinued_replacements)
        .map(replacement => `<a href="${replacement.url}">${replacement.sku}</a>`)
        .join(', ');
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.product-variation-thumbnail {
  flex: 1 0 auto;
  width: 100%;
  height: 100%;
  display: flex;
  aspect-ratio: 1;

  @media (min-width: 768px) {
    max-width: 102px;
    max-height: 102px;
  }

  .field--name-field-image,
  img {
    width: 100%;
    object-fit: cover;

    @media (min-width: 768px) {
      max-width: 102px;
      max-height: 102px;
    }
  }
}

.discontinued-replacements {
  font-weight: bold;
}

.product-gate {
  + .slo-button {
    margin-top: 0.8rem;
  }

  &:empty + .slo-button {
    margin-top: 0;
  }
}

.unit-of-measure {
  font-size: 2.2rem
}

.channel-advisor-modal {
  position: fixed;
  z-index: 20000;
  inset: 0;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(#dedede, 0.8);
}
.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.channel-advisor-modal--content {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  place-items: end;
  gap: 16px;
  height: auto;
  width: calc(min(100% - 30px, 800px));
  margin-inline: auto;
  padding-block: 16px;
}
.channel-advisor-modal--iframe {
  width: 100%;
  height: 100%;
  min-height: 667px;
  border-radius: 12px;
  @media (min-width: 710px) {
    min-height: 600px;
  }
}
.channel-advisor-modal--close {
  display: flex;
  height: 16px;
  color: rgba($color-gray-900, 0.7);
  font-weight: bold;
  cursor: pointer;
  transition: color 0.2s ease;
  &:hover {
    color: rgba($color-gray-900, 1);
  }
}
</style>
